import { CdkDrag, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  model,
  output,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiAccordionModule } from '@siemens/element-ng/accordion';
import { SiCardModule } from '@siemens/element-ng/card';
import { SI_DATATABLE_CONFIG } from '@siemens/element-ng/datatable';
import { SiEmptyStateModule } from '@siemens/element-ng/empty-state';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiIconModule } from '@siemens/element-ng/icon';
import { MenuItem, SiMenuModule } from '@siemens/element-ng/menu';
import { SiModalService } from '@siemens/element-ng/modal';
import { SiNumberInputModule } from '@siemens/element-ng/number-input';
import { SelectItem, SiSelectModule } from '@siemens/element-ng/select';
import { SiTooltipDirective } from '@siemens/element-ng/tooltip';
import { NgxDatatableModule } from '@siemens/ngx-datatable';
import {
  DrivingData,
  Schedule,
  StationData,
  TrackDataSet,
  TrackInSchedule
} from 'src/app/main/shared/DataTypes';
import { StarPrefixPipe } from 'src/app/main/shared/pipes/star-prefix.pipe';
import { EventService } from 'src/app/main/shared/services/event.service';
import { ShareDataService } from 'src/app/main/shared/services/share-data.service';

import { TrackService } from '../../track/track.service';
import { ScheduleService } from '../schedule.service';

@Component({
  selector: 'app-schedule-table',
  imports: [
    SiAccordionModule,
    NgxDatatableModule,
    SiNumberInputModule,
    FormsModule,
    SiIconModule,
    SiFormModule,
    SiCardModule,
    SiMenuModule,
    TranslateModule,
    SiEmptyStateModule,
    CdkMenuTrigger,
    SiSelectModule,
    CdkDrag,
    CdkDropList,
    CdkDragHandle,
    StarPrefixPipe,
    SiTooltipDirective
  ],
  standalone: true, // Add standalone: true if not already
  templateUrl: './schedule-table.component.html',
  styleUrl: './schedule-table.component.scss'
})
export class ScheduleTableComponent implements AfterViewInit {
  scheduleService = inject(ScheduleService);
  trackService = inject(TrackService);
  eventService = inject(EventService);
  shareDataService = inject(ShareDataService);
  modalService = inject(SiModalService);
  translateService = inject(TranslateService);

  readonly selectedSchedule = model.required<Schedule | undefined>();
  readonly removeTrackEvent = output<number>();
  readonly openLoadTrackModalClicked = output<void>();

  readonly trackStationsValidChange = output<boolean>();
  readonly trackVersionsAccessibleChange = output<boolean>();
  constructor() {
    // Set up an effect to emit changes back to the parent component
    effect(() => {
      this.trackStationsValidChange.emit(this.scheduleTrackStationsValid());
    });

    effect(() => {
      this.trackVersionsAccessibleChange.emit(this.allTrackVersionsAccessible());
    });

    // Load track versions when schedule changes
    effect(() => {
      if (this.selectedSchedule()) {
        this.loadTrackVersions();
      }
    });
  }

  ngAfterViewInit(): void {
    // Receive Driving/Stopping Actions when Vehicle or Scenario gets changed
    this.eventService.actionsForVehicleChangedEvent$.subscribe((data: any) => {
      this.setDrivingAndStoppingActions(data);
    });

    // Receive current driving/stopping actions
    this.setDrivingAndStoppingActions(this.eventService.getLastActionsForVehicle());
  }

  tableConfig = SI_DATATABLE_CONFIG;
  readonly availableDrivingActions = signal<SelectItem<any>[]>([]);
  readonly availableStoppingActions = signal<SelectItem<any>[]>([]);

  // Unified track version data management - Using signal for change detection
  private trackVersionDataMap = new Map<
    number,
    {
      options: SelectItem<number>[];
      selectedTrackId: number;
      scheduleLinkId: number;
      isLoading: boolean;
      accessible: boolean;
      latestKnownVersionId?: number;
    }
  >();

  // Signal to trigger change detection when trackVersionData changes
  private readonly trackVersionDataSignal = signal<number>(0);

  // Quick lookup: track index -> schedule_link_id (used in template)
  readonly linkIdByTrackIndex = signal<number[]>([]);

  // False while any linked track version comes from a workspace the user cannot access
  readonly allTrackVersionsAccessible = computed(() => {
    this.trackVersionDataSignal();
    for (const data of this.trackVersionDataMap.values()) {
      if (!data.accessible) {
        return false;
      }
    }
    return true;
  });

  readonly scheduleTrackStationsValid = computed(() => {
    if (!this.selectedSchedule()) {
      return false;
    }
    const tracks = this.selectedSchedule()!.tracks;
    if (tracks.length === 0) {
      return false;
    }
    for (let i = 0; i < tracks.length - 1; i++) {
      const lastStationOfCurrentTrack =
        tracks[i].scheduleData.stoppingData[tracks[i].scheduleData.stoppingData.length - 1];
      const firstStationOfNextTrack = tracks[i + 1].scheduleData.stoppingData[0];
      // Check if the last station of the current track is the same as the first station of the next track
      if (lastStationOfCurrentTrack.stationName !== firstStationOfNextTrack.stationName) {
        return false;
      }
    }
    return true;
  });
  setDrivingAndStoppingActions(data: any) {
    if (data.drivingActions?.length > 0) {
      this.availableDrivingActions.set(
        data.drivingActions.map((action: any) => ({
          type: 'option',
          value: action,
          label: action
        }))
      );
    }
    if (data.stoppingActions?.length > 0) {
      const stoppingActionOptions = data.stoppingActions.map((action: any) => ({
        type: 'option',
        value: action,
        label: action
      }));
      // Add clear selection option at the beginning
      const clearOption = {
        type: 'option',
        value: 'None',
        label: 'None'
      };

      this.availableStoppingActions.set([clearOption, ...stoppingActionOptions]);
    }
  }

  onTrackDrop(event: any) {
    // Change the order of the tracks
    const tracks = [...this.selectedSchedule()!.tracks];
    moveItemInArray(tracks, event.previousIndex, event.currentIndex);

    this.selectedSchedule.update(() => {
      return { ...this.selectedSchedule()!, tracks: tracks };
    });

    // Rebuild linkIdByTrackIndex to match the new track order
    this.rebuildLinkIdMapping();
  }

  private rebuildLinkIdMapping() {
    if (!this.selectedSchedule()) return;

    const newMapping: number[] = [];

    this.selectedSchedule()!.tracks.forEach((track, idx) => {
      const trackId =
        track.scheduleData.drivingData[0]?.trackId ?? track.scheduleData.stoppingData[0]?.trackId;

      if (!trackId) return;

      // Find the scheduleLinkId from trackVersionData by matching selectedTrackId
      for (const [scheduleLinkId, data] of this.trackVersionDataMap.entries()) {
        if (data.selectedTrackId === trackId) {
          newMapping[idx] = scheduleLinkId;
          break;
        }
      }
    });

    this.linkIdByTrackIndex.set(newMapping);
  }

  contextMenuItems: MenuItem[] = [
    {
      label: this.translateService.instant('SHARED.REMOVE'),
      type: 'action',
      action: (index: number) => this.removeTrackClicked(index)
    },
    {
      label: this.translateService.instant('SHARED.EDIT'),
      type: 'action',
      action: (index: number) => this.openTrackFromScheduleInTracksPanel(index)
    }
  ];

  openTrackFromScheduleInTracksPanel(index: number) {
    const schedule = this.selectedSchedule()!;
    if (schedule.tracks[index].trackParameters) {
      const trackName = schedule.tracks[index].scheduleData.drivingData[0].trackName;
      this.eventService.openTrackFromSchedule(
        undefined,
        schedule.tracks[index].trackParameters,
        trackName
      );
    } else {
      const trackId = schedule.tracks[index].scheduleData.drivingData[0].trackId;
      this.eventService.openTrackFromSchedule(trackId);
    }
  }

  removeTrackClicked(index: number) {
    this.removeTrackEvent.emit(index);
  }

  reverseSchedule() {
    const schedule = this.selectedSchedule()!;
    // Reverse the order of tracks in the schedule
    const reversedTracks = schedule.tracks.slice().reverse();

    this.selectedSchedule.set({
      ...this.selectedSchedule()!,
      id: undefined,
      tracks: reversedTracks
    });
  }

  loadTrackVersions() {
    const scheduleId = this.selectedSchedule()?.id;
    if (!scheduleId) {
      this.loadTrackVersionsFromWorkspaces();
      return;
    }
    this.trackService.getConnectedTracksFromScheduleId(scheduleId).subscribe(apiTracksResponse => {
      this.trackVersionDataMap.clear();
      const newLinkIds: number[] = [];

      if (!apiTracksResponse) return;

      // Build a map from trackId to API response data for quick lookup
      // We need to check all relatedVersions, not just the current trackId
      const apiTrackByTrackId = new Map<number, any>();
      apiTracksResponse.forEach((apiTrack: any) => {
        // Map the current trackId
        apiTrackByTrackId.set(apiTrack.trackId, apiTrack);
        // Also map all related version trackIds to the same apiTrack
        apiTrack.relatedVersions?.forEach((version: any) => {
          apiTrackByTrackId.set(version.trackId, apiTrack);
        });
      });

      // Match API response to actual schedule tracks in their current order
      this.selectedSchedule()!.tracks.forEach((track, idx) => {
        // Get the trackId from the first driving/stopping data entry
        const trackId =
          track.scheduleData.drivingData[0]?.trackId ?? track.scheduleData.stoppingData[0]?.trackId;

        if (!trackId) return;

        const apiTrack = apiTrackByTrackId.get(trackId);
        if (!apiTrack) return;

        const scheduleLinkId: number = apiTrack.scheduleLinkId;
        newLinkIds[idx] = scheduleLinkId;

        const optionsForThisGroup = apiTrack.relatedVersions.map((v: any) => ({
          type: 'option' as const,
          label: v.version,
          value: v.trackId
        }));

        // Cache a simple change identifier (max trackId among relatedVersions)
        const latestId = Math.max(
          0,
          ...(apiTrack.relatedVersions ?? []).map((v: any) => v.trackId ?? 0)
        );

        // Store all data together in unified structure
        this.trackVersionDataMap.set(scheduleLinkId, {
          options: optionsForThisGroup,
          selectedTrackId: trackId,
          scheduleLinkId: scheduleLinkId,
          isLoading: false,
          accessible: apiTrack.accessible !== false,
          latestKnownVersionId: latestId
        });
      });

      // Update signals to trigger change detection
      this.linkIdByTrackIndex.set(newLinkIds);
      this.trackVersionDataSignal.update(v => v + 1);
    });
  }

  // Unsaved schedules have no ScheduleTrackLink rows yet, so versions come from the workspace tree
  private loadTrackVersionsFromWorkspaces() {
    this.trackVersionDataMap.clear();
    const newLinkIds: number[] = [];
    const trackGroups = this.shareDataService
      .availableWorkspaces()
      .flatMap(workspace => workspace.tracks);

    this.selectedSchedule()?.tracks.forEach((track, idx) => {
      const trackId =
        track.scheduleData.drivingData[0]?.trackId ?? track.scheduleData.stoppingData[0]?.trackId;
      if (!trackId) return;

      const trackGroup = trackGroups.find(group =>
        group.variants.some(variant => variant.id === trackId)
      );
      if (!trackGroup) return;

      // Negative keys keep these apart from real schedule_link_ids
      const localLinkId = -(idx + 1);
      newLinkIds[idx] = localLinkId;

      this.trackVersionDataMap.set(localLinkId, {
        options: trackGroup.variants
          .filter(
            variant => variant.id !== undefined && (!variant.deleted || variant.id === trackId)
          )
          .map(variant => ({
            type: 'option' as const,
            label: variant.version ?? '',
            value: variant.id!
          })),
        selectedTrackId: trackId,
        scheduleLinkId: localLinkId,
        isLoading: false,
        accessible: true
      });
    });

    this.linkIdByTrackIndex.set(newLinkIds);
    this.trackVersionDataSignal.update(v => v + 1);
  }

  // Re-query only the options for a single schedule link (called when user opens the dropdown)
  refreshTrackVersionsForLink(scheduleLinkId: number) {
    const scheduleId = this.selectedSchedule()?.id;
    if (!scheduleId) return;

    const currentData = this.trackVersionDataMap.get(scheduleLinkId);

    this.trackService.getConnectedTracksFromScheduleId(scheduleId).subscribe({
      next: apiTracksResponse => {
        if (!apiTracksResponse) return;
        const apiTrack = apiTracksResponse.find(t => t.scheduleLinkId === scheduleLinkId);
        if (!apiTrack) return;

        // Compute a lightweight identifier for change detection
        const latestId = Math.max(0, ...(apiTrack.relatedVersions ?? []).map(v => v.trackId ?? 0));

        // If unchanged, skip applying updates
        if (
          currentData?.latestKnownVersionId !== undefined &&
          currentData.latestKnownVersionId === latestId
        ) {
          return;
        }

        const optionsForThisGroup = apiTrack.relatedVersions.map(v => ({
          type: 'option' as const,
          label: v.version,
          value: v.trackId
        }));

        // Update all data atomically
        this.trackVersionDataMap.set(scheduleLinkId, {
          options: optionsForThisGroup,
          selectedTrackId: apiTrack.trackId,
          scheduleLinkId: scheduleLinkId,
          isLoading: false,
          accessible: apiTrack.accessible !== false,
          latestKnownVersionId: latestId
        });
        this.trackVersionDataSignal.update(v => v + 1);
      }
    });
  }

  // Called from the template when the user clicks the version select for a track
  onDropdownClick(trackIndex: number) {
    const scheduleLinkId = this.linkIdByTrackIndex()[trackIndex];
    if (!scheduleLinkId) {
      // fallback: reload all if mapping not initialized
      this.loadTrackVersions();
      return;
    }
    this.refreshTrackVersionsForLink(scheduleLinkId);
  }

  getTrackVersionOptionsByLinkId(scheduleLinkId: number) {
    // Access signal to trigger change detection
    this.trackVersionDataSignal();
    return this.trackVersionDataMap.get(scheduleLinkId)?.options ?? [];
  }

  getSelectedVersionByLinkId(scheduleLinkId: number) {
    // Access signal to trigger change detection
    this.trackVersionDataSignal();
    return this.trackVersionDataMap.get(scheduleLinkId)?.selectedTrackId;
  }

  isTrackVersionChangePending(scheduleLinkId: number): boolean {
    this.trackVersionDataSignal();
    return this.trackVersionDataMap.get(scheduleLinkId)?.isLoading ?? false;
  }

  isTrackVersionAccessible(scheduleLinkId: number): boolean {
    this.trackVersionDataSignal();
    return this.trackVersionDataMap.get(scheduleLinkId)?.accessible ?? true;
  }

  hasTrackVersions(scheduleLinkId: number): boolean {
    this.trackVersionDataSignal();
    const options = this.trackVersionDataMap.get(scheduleLinkId)?.options;
    return options !== undefined && options.length > 0;
  }

  onTrackVersionChangeByLinkId(scheduleLinkId: number, newTrackId: number) {
    const data = this.trackVersionDataMap.get(scheduleLinkId);
    if (!data) return;

    const previousTrackId = data.selectedTrackId;
    if (previousTrackId === newTrackId) return; // no-op

    // Optimistic UI update - mark as loading
    this.trackVersionDataMap.set(scheduleLinkId, {
      ...data,
      selectedTrackId: newTrackId,
      isLoading: true
    });
    this.trackVersionDataSignal.update(v => v + 1);
    const id = this.selectedSchedule()?.id;
    if (!id) {
      // Nothing to persist yet - the link row is created when the schedule is saved
      this.trackVersionDataMap.set(scheduleLinkId, {
        ...data,
        selectedTrackId: newTrackId,
        isLoading: false
      });
      this.trackVersionDataSignal.update(v => v + 1);
      this.applyTrackVersionChangeInSchedule(previousTrackId, newTrackId);
      return;
    }
    if (id) {
      this.trackService.changeLinkedTrackInSchedule(id, scheduleLinkId, newTrackId).subscribe({
        next: response => {
          const currentData = this.trackVersionDataMap.get(scheduleLinkId);
          if (!currentData) return;

          if (!response.success) {
            // Revert to previous value
            this.trackVersionDataMap.set(scheduleLinkId, {
              ...currentData,
              selectedTrackId: previousTrackId,
              isLoading: false
            });
            this.trackVersionDataSignal.update(v => v + 1);
          } else {
            // Success: update isLoading flag and in-memory schedule trackId occurrences
            this.trackVersionDataMap.set(scheduleLinkId, {
              ...currentData,
              isLoading: false,
              accessible: true
            });
            this.trackVersionDataSignal.update(v => v + 1);

            this.applyTrackVersionChangeInSchedule(previousTrackId, newTrackId);
          }
        },
        error: err => {
          const currentData = this.trackVersionDataMap.get(scheduleLinkId);
          if (currentData) {
            // Revert to previous value
            this.trackVersionDataMap.set(scheduleLinkId, {
              ...currentData,
              selectedTrackId: previousTrackId,
              isLoading: false
            });
            this.trackVersionDataSignal.update(v => v + 1);
          }
        }
      });
    }
  }

  private applyTrackVersionChangeInSchedule(previousTrackId: number, newTrackId: number) {
    this.trackService.getTrackDataFromDB(newTrackId).subscribe({
      next: parameters =>
        this.replaceTrackVersionRows(
          previousTrackId,
          newTrackId,
          parameters as { [key: string]: TrackDataSet[] }
        ),
      // Without the new track data at least the ids have to stay consistent
      error: () => this.replaceTrackVersionRows(previousTrackId, newTrackId)
    });
  }

  private replaceTrackVersionRows(
    previousTrackId: number,
    newTrackId: number,
    parameters?: { [key: string]: TrackDataSet[] }
  ) {
    const schedule = this.selectedSchedule();
    if (!schedule) return;

    const stations = parameters?.['station_data'];
    const tracks = schedule.tracks.map(trackInSchedule => {
      const belongsToChangedTrack =
        trackInSchedule.scheduleData.stoppingData.some(s => s.trackId === previousTrackId) ||
        trackInSchedule.scheduleData.drivingData.some(d => d.trackId === previousTrackId);
      if (!belongsToChangedTrack) return trackInSchedule;

      if (!parameters || !stations?.length) {
        return {
          ...trackInSchedule,
          scheduleData: {
            drivingData: trackInSchedule.scheduleData.drivingData.map(d =>
              d.trackId === previousTrackId ? { ...d, trackId: newTrackId } : d
            ),
            stoppingData: trackInSchedule.scheduleData.stoppingData.map(s =>
              s.trackId === previousTrackId ? { ...s, trackId: newTrackId } : s
            )
          }
        };
      }

      const rebuilt = this.scheduleService.getScheduleTracksFromTrackVariant({
        id: newTrackId,
        name: trackInSchedule.scheduleData.stoppingData[0]?.trackName ?? '',
        deleted: false,
        source: 'Database',
        parameters,
        tags: []
      });
      this.carryOverScheduleEdits(trackInSchedule, rebuilt);
      return rebuilt;
    });

    this.selectedSchedule.set({ ...schedule, tracks });
  }

  // Keeps stopping times and actions the user entered for stations the new variant still contains
  private carryOverScheduleEdits(previous: TrackInSchedule, rebuilt: TrackInSchedule) {
    const stopsByStation = new Map<string, StationData[]>();
    previous.scheduleData.stoppingData.forEach(stop => {
      const stops = stopsByStation.get(stop.stationName) ?? [];
      stops.push(stop);
      stopsByStation.set(stop.stationName, stops);
    });
    rebuilt.scheduleData.stoppingData.forEach(stop => {
      const previousStop = stopsByStation.get(stop.stationName)?.shift();
      if (!previousStop) return;
      stop.stoppingTime = previousStop.stoppingTime;
      stop.stoppingActions = [...previousStop.stoppingActions];
      stop.doNotStopAtStation = previousStop.doNotStopAtStation;
    });

    const legKey = (leg: DrivingData) => `${leg.fromStation}\u0000${leg.toStation}`;
    const legsByStations = new Map<string, DrivingData[]>();
    previous.scheduleData.drivingData.forEach(leg => {
      const legs = legsByStations.get(legKey(leg)) ?? [];
      legs.push(leg);
      legsByStations.set(legKey(leg), legs);
    });
    rebuilt.scheduleData.drivingData.forEach(leg => {
      const previousLeg = legsByStations.get(legKey(leg))?.shift();
      if (!previousLeg) return;
      leg.drivingTime = previousLeg.drivingTime;
      leg.drivingActions = [...previousLeg.drivingActions];
    });
  }

  onStoppingActionChange(stoppingData: any, newValue: any) {
    // Always keep stoppingActions as string[] (DataTypes expects string[])
    // Clear selection -> empty array
    if (newValue === 'None' || newValue == null) {
      stoppingData.stoppingActions = [];
    } else {
      stoppingData.stoppingActions = [newValue];
    }
  }

  getStoppingActionDisplayValue(stoppingData: any) {
    // Display first selected action (single select). Return null if none.
    return stoppingData?.stoppingActions && stoppingData.stoppingActions.length > 0
      ? stoppingData.stoppingActions[0]
      : null;
  }
}
